/**
 * ChatComposerInput — the text field + send button shared by every chat
 * composer: the full conversation thread (MessageThread: drawer, DMs, support)
 * and the inline game-log chat (GameChatPanel). One component so the two
 * can't drift on input limits, send rules or disabled behaviour.
 *
 *   - `bubble`  — the thread composer: rounded field, 38 px send button.
 *   - `bar`     — the inline game chat bar under the map controls: a narrower
 *                 bordered field and a 30 px send button, matching the bar's
 *                 zoom pads so it reads as part of the same control strip.
 *
 * Purely presentational: the caller owns the text, send and error state.
 */
import React, { useEffect, useState } from 'react'
import { Platform, TextInput, View, type NativeSyntheticEvent, type TextInputKeyPressEventData } from 'react-native'
import { useTheme } from '../../hooks/useTheme'
import { ChatSendButton } from '../../../../packages/ui/src/components/ChatSendButton'
import { barInputHeight, isWebSendKey } from '../../lib/inlineChat'

export const CHAT_MESSAGE_MAX_LENGTH = 500

export interface ChatComposerInputProps {
  variant: 'bubble' | 'bar'
  inputRef?: React.Ref<TextInput>
  value: string
  onChangeText: (text: string) => void
  onSend: () => void
  onFocus?: () => void
  onBlur?: () => void
  placeholder: string
  disabled?: boolean
  isSending?: boolean
  testID?: string
}

export function ChatComposerInput({
  variant,
  inputRef,
  value,
  onChangeText,
  onSend,
  onFocus,
  onBlur,
  placeholder,
  disabled = false,
  isSending = false,
  testID,
}: ChatComposerInputProps) {
  const { theme } = useTheme()
  const placeholderHex = theme === 'dark' ? '#8a8c6e' : '#6b5b3e'
  const sendColor = theme === 'dark' ? '#e0463f' : '#a01818'
  const isBar = variant === 'bar'
  // Bar variant: one line tall, growing a line at a time as text wraps.
  const barLineHeight = Platform.OS === 'web' ? 20 : 18
  const [barHeight, setBarHeight] = useState(() => barInputHeight(0, barLineHeight, true))
  // Back to one line when the field empties (after Send). The content-size
  // event can't be relied on to shrink: on web a cleared textarea keeps
  // reporting its current (grown) height.
  useEffect(() => {
    if (isBar && value === '') setBarHeight(barInputHeight(0, barLineHeight, true))
  }, [isBar, value, barLineHeight])

  // Inline log composer on web: Enter sends (desktop expectation). The thread
  // composer keeps its existing behaviour (button / native submit).
  const handleKeyPress = isBar && Platform.OS === 'web'
    ? (event: NativeSyntheticEvent<TextInputKeyPressEventData>) => {
        const native = event.nativeEvent as TextInputKeyPressEventData & { shiftKey?: boolean }
        if (isWebSendKey(native.key, native.shiftKey)) {
          ;(event as unknown as { preventDefault?: () => void }).preventDefault?.()
          onSend()
        }
      }
    : undefined

  const input = (
    <TextInput
      ref={inputRef}
      testID={testID}
      value={disabled ? '' : value}
      editable={!disabled}
      aria-disabled={disabled}
      caretHidden={disabled}
      accessibilityLabel={placeholder}
      onFocus={onFocus}
      onBlur={onBlur}
      onChangeText={onChangeText}
      onKeyPress={handleKeyPress}
      placeholder={placeholder}
      placeholderTextColor={placeholderHex}
      maxLength={CHAT_MESSAGE_MAX_LENGTH}
      multiline
      // Start at a single row (RN-web otherwise renders a 2-row textarea).
      numberOfLines={isBar ? 1 : undefined}
      onContentSizeChange={isBar
        ? (event) => setBarHeight(barInputHeight(event.nativeEvent.contentSize.height, barLineHeight, true))
        : undefined}
      returnKeyType="send"
      blurOnSubmit={false}
      onSubmitEditing={Platform.OS !== 'web' ? onSend : undefined}
      className={isBar ? 'bg-canvas text-ink font-typed border border-olive' : 'bg-paper text-ink font-typed border border-olive'}
      style={isBar
        ? {
            flex: 1,
            borderRadius: 6,
            // Text starts 24 px from the column edge (8 bar inset + 1 border +
            // 15) — the same x as the log lines' text below (12 row padding +
            // 6 dot + 6 gap).
            paddingLeft: 15,
            paddingRight: 10,
            paddingTop: 5,
            paddingBottom: 5,
            height: barHeight,
            // 16 px on web: mobile Safari zooms the page into smaller fields.
            fontSize: Platform.OS === 'web' ? 16 : 14,
            lineHeight: barLineHeight,
          }
        : {
            flex: 1,
            borderRadius: 20,
            paddingHorizontal: 16,
            paddingTop: 10,
            paddingBottom: 10,
            fontSize: 16,
            maxHeight: 100,
            lineHeight: 22,
          }}
    />
  )

  // Web: pressing Send must not take focus from the field. A blur there
  // closed the keyboard fit and re-laid out the screen mid-tap, so the press
  // missed the (moved) button and the first Send did nothing. Cancelling the
  // pointerdown default keeps focus in the field; the click still fires.
  const keepFocusOnPress = Platform.OS === 'web'
    ? { onPointerDown: (event: { preventDefault: () => void }) => event.preventDefault() }
    : null
  const send = (
    <View {...keepFocusOnPress} testID={isBar ? 'inline-chat-send' : undefined}>
      <ChatSendButton
        onPress={onSend}
        disabled={disabled || !value.trim()}
        loading={isSending}
        color={sendColor}
        size={isBar ? 30 : 38}
      />
    </View>
  )

  if (!isBar) {
    return (
      <>
        {input}
        {send}
      </>
    )
  }
  return (
    <View className="flex-row items-center" style={{ gap: 6 }}>
      {input}
      {send}
    </View>
  )
}
